import { Stack, Typography } from "@mui/material";
import { useSearchParams } from "react-router";

import GaiaPageLayoutColumn from "~/modules/app/components/GaiaPageLayoutColumn";

import useRequireCurrentProject from "../../hooks/useRequireCurrentProject";
import { ProjectDeliverablesFilters } from "./components/ProjectDeliverablesFilters";
import { ProjectDeliverablesMap } from "./components/ProjectDeliverablesMap";
import { ProjectDeliverableTable } from "./components/ProjectDeliverableTable";
import { useProjectDeliverablesFilterOptions } from "./useProjectDeliverablesFilterOptions";
import { useProjectGetDeliverables } from "./useProjectGetDeliverables";

export const RESEARCH_TYPE_ID_PARAM = "researchTypeId";
export const PLANNER_AREA_ID_PARAM = "plannerAreaId";

export function ProjectDeliverablesScreen() {
    const { project } = useRequireCurrentProject();

    const [searchParams, setSearchParams] = useSearchParams();
    const researchTypeId = searchParams.get(RESEARCH_TYPE_ID_PARAM);
    const plannerAreaIds = searchParams.getAll(PLANNER_AREA_ID_PARAM);

    const { researchTypes, plannerAreas } = useProjectDeliverablesFilterOptions(project.id);

    return (
        <GaiaPageLayoutColumn maxWidth="lg">
            <Typography variant="h1">Project Deliverables</Typography>
            <ProjectDeliverablesFilters
                researchTypes={researchTypes}
                plannerAreas={plannerAreas}
                selectedResearchTypeIdFilter={researchTypeId}
                onChangeResearchTypeId={newResearchTypeId => {
                    setSearchParams(params => {
                        params.set(RESEARCH_TYPE_ID_PARAM, newResearchTypeId);
                        return params;
                    });
                }}
                selectedPlannerAreaIdsFilter={plannerAreaIds}
                onChangePlannerAreaIds={(values: string[]) => {
                    setSearchParams(prev => {
                        const next = new URLSearchParams(prev);
                        next.delete(PLANNER_AREA_ID_PARAM);
                        values.forEach(value => {
                            next.append(PLANNER_AREA_ID_PARAM, value);
                        });
                        return next;
                    });
                }}
            />

            {plannerAreaIds.length > 0 && researchTypeId && (
                <ProjectDeliverablesScreenWithFilterValues
                    projectId={project.id}
                    researchTypeId={researchTypeId}
                    plannerAreaIds={plannerAreaIds}
                />
            )}
        </GaiaPageLayoutColumn>
    );
}

function ProjectDeliverablesScreenWithFilterValues({
    projectId,
    researchTypeId,
    plannerAreaIds,
}: {
    projectId: string;
    researchTypeId: string;
    plannerAreaIds: string[];
}) {
    const { maps, tables } = useProjectGetDeliverables(projectId, researchTypeId, plannerAreaIds);

    return (
        <Stack direction="column" gap={2}>
            {maps !== undefined && maps.length > 0 && maps.map(map => <ProjectDeliverablesMap key={map.title} map={map} />)}
            {tables !== undefined &&
                tables.length > 0 &&
                tables.map(element => <ProjectDeliverableTable key={element.title} table={element} />)}
        </Stack>
    );
}
